import { supabase } from '@/integrations/supabase/client';

export interface GameParticipant {
  id: string;
  game_id: string;
  player_id: string;
  player_type: 'supabase' | 'internal';
  team_side: 'home' | 'away';
  participation_status: 'requested' | 'approved' | 'denied' | 'ready_to_play';
  position?: string;
  eligibility_status: 'eligible' | 'ineligible';
  eligibility_notes?: string;
  requested_by: string;
  confirmed_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerRequest {
  id: string;
  player_id: string;
  player_name: string;
  player_type: 'supabase' | 'internal';
  game_id: string;
  game_title: string;
  team_side: 'home' | 'away';
  participation_status: string;
  requested_at: string;
}

export const gameParticipantsService = {
  // Get pending player requests for games the user can manage
  async getPendingPlayerRequests(userId: string): Promise<PlayerRequest[]> {
    console.log('Getting pending player requests for user:', userId);
    
    try {
      // Get teams the user can manage (as coach or owner)
      const { data: userTeams, error: teamsError } = await supabase
        .from('team_coaches')
        .select('team_id')
        .eq('coach_id', userId);

      if (teamsError) {
        console.error('Error fetching user teams:', teamsError);
        return [];
      }

      const teamIds = userTeams?.map(t => t.team_id) || [];

      // Also check for teams owned by the user
      const { data: ownedTeams, error: ownedTeamsError } = await supabase
        .from('teams')
        .select('team_id')
        .eq('created_by', userId);

      if (ownedTeamsError) {
        console.error('Error fetching owned teams:', ownedTeamsError);
      } else {
        teamIds.push(...(ownedTeams?.map(t => t.team_id) || []));
      }

      if (teamIds.length === 0) {
        return [];
      }

      // Get pending requests for games involving user's teams
      const { data: requests, error } = await supabase
        .from('game_participants')
        .select(`
          id,
          game_id,
          player_id,
          player_type,
          team_side,
          participation_status,
          created_at,
          venue_games:venue_games!inner(id, game_title, home_team_id, away_team_id)
        `)
        .eq('participation_status', 'requested')
        .or(`venue_games.home_team_id.in.(${teamIds.join(',')}),venue_games.away_team_id.in.(${teamIds.join(',')})`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching player requests:', error);
        return [];
      }

      // Transform the data to include player names
      const requestsWithPlayerNames = await Promise.all(
        (requests || []).map(async (request) => {
          let playerName = 'Unknown Player';
          
          if (request.player_type === 'supabase') {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, username')
              .eq('id', request.player_id)
              .single();
            
            if (profile) {
              playerName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'Unknown Player';
            }
          } else {
            const { data: internalUser } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', request.player_id)
              .single();
            
            if (internalUser) {
              playerName = internalUser.username;
            }
          }

          return {
            id: request.id,
            player_id: request.player_id,
            player_name: playerName,
            player_type: request.player_type as 'supabase' | 'internal',
            game_id: request.game_id,
            game_title: (request as any).venue_games?.game_title || 'Unknown Game',
            team_side: request.team_side as 'home' | 'away',
            participation_status: request.participation_status,
            requested_at: request.created_at
          };
        })
      );

      console.log('Pending player requests:', requestsWithPlayerNames);
      return requestsWithPlayerNames;
    } catch (error) {
      console.error('Error getting pending player requests:', error);
      return [];
    }
  },

  // Approve or deny a player request
  async respondToPlayerRequest(
    requestId: string, 
    decision: 'approved' | 'denied', 
    responderId: string,
    notes?: string
  ): Promise<boolean> {
    console.log('Responding to player request:', { requestId, decision, responderId });
    
    try {
      const { error } = await supabase
        .from('game_participants')
        .update({
          participation_status: decision,
          confirmed_by: responderId,
          eligibility_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        console.error('Error responding to player request:', error);
        return false;
      }

      console.log('Successfully responded to player request');
      return true;
    } catch (error) {
      console.error('Error responding to player request:', error);
      return false;
    }
  },

  // Get all participants for a specific game
  async getGameParticipants(gameId: string): Promise<GameParticipant[]> {
    console.log('Getting participants for game:', gameId);
    
    try {
      const { data, error } = await supabase
        .from('game_participants')
        .select('*')
        .eq('game_id', gameId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching game participants:', error);
        return [];
      }

      console.log('Game participants:', data);
      return (data || []) as GameParticipant[];
    } catch (error) {
      console.error('Error getting game participants:', error);
      return [];
    }
  },

  // Remove player from game (self-removal)
  async removePlayerSelfFromGame(
    gameId: string, 
    playerId: string,
    playerType: 'supabase' | 'internal'
  ): Promise<boolean> {
    console.log('Player removing themselves from game:', { gameId, playerId, playerType });
    
    try {
      // Remove from game_participants
      const { error: participantError } = await supabase
        .from('game_participants')
        .delete()
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (participantError) {
        console.error('Error removing from game_participants:', participantError);
        return false;
      }

      // Remove attendance status
      const { error: attendanceError } = await supabase
        .from('player_attendance_status')
        .delete()
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (attendanceError) {
        console.error('Error removing attendance status:', attendanceError);
        // Don't fail the whole operation if attendance removal fails
      }

      // Remove readiness status if exists
      const { error: readinessError } = await supabase
        .from('player_readiness_status')
        .delete()
        .eq('game_id', gameId)
        .eq('player_id', playerId);

      if (readinessError) {
        console.error('Error removing readiness status:', readinessError);
        // Don't fail the whole operation if readiness removal fails
      }

      // Create notification for coaches/team owners
      const { data: gameData } = await supabase
        .from('venue_games')
        .select('home_team_id, away_team_id, game_title')
        .eq('id', gameId)
        .single();

      if (gameData) {
        // Get player name for notification
        let playerName = 'A player';
        if (playerType === 'supabase') {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, username')
            .eq('id', playerId)
            .single();
          
          if (profile) {
            playerName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'A player';
          }
        } else {
          const { data: internalUser } = await supabase
            .from('internal_users')
            .select('username')
            .eq('id', playerId)
            .single();
          
          if (internalUser) {
            playerName = internalUser.username;
          }
        }

        // Send notifications to coaches and team owners
        const teamIds = [gameData.home_team_id, gameData.away_team_id].filter(Boolean);
        
        for (const teamId of teamIds) {
          // Notify coaches
          const { data: coaches } = await supabase
            .from('team_coaches')
            .select('coach_id')
            .eq('team_id', teamId);

          for (const coach of coaches || []) {
            await supabase
              .from('game_notifications')
              .insert({
                game_id: gameId,
                recipient_id: coach.coach_id,
                recipient_type: 'supabase',
                notification_type: 'player_left_game',
                message: `${playerName} has left the game "${gameData.game_title}". Please review your roster.`,
                requires_action: false
              });
          }

          // Notify team owner
          const { data: team } = await supabase
            .from('teams')
            .select('created_by')
            .eq('team_id', teamId)
            .single();

          if (team?.created_by) {
            await supabase
              .from('game_notifications')
              .insert({
                game_id: gameId,
                recipient_id: team.created_by,
                recipient_type: 'supabase',
                notification_type: 'player_left_game',
                message: `${playerName} has left the game "${gameData.game_title}". Please review your roster.`,
                requires_action: false
              });
          }
        }
      }

      console.log('Successfully removed player from game');
      return true;
    } catch (error) {
      console.error('Error removing player from game:', error);
      return false;
    }
  },

  // Cancel join request (pending requests only)
  async cancelJoinRequest(
    gameId: string, 
    playerId: string
  ): Promise<boolean> {
    console.log('Canceling join request:', { gameId, playerId });
    
    try {
      const { error } = await supabase
        .from('game_participants')
        .delete()
        .eq('game_id', gameId)
        .eq('player_id', playerId)
        .eq('participation_status', 'requested');

      if (error) {
        console.error('Error canceling join request:', error);
        return false;
      }

      console.log('Successfully canceled join request');
      return true;
    } catch (error) {
      console.error('Error canceling join request:', error);
      return false;
    }
  }
};